import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
} from 'antd';
import { getDashboardStatistics } from '../../services/statistics';
import './styles.css';
import {
  PersonalLeadDistributionChart,
  LeadIntentionDistributionChart,
} from './components';

const { Title, Text } = Typography;

// Dashboard statistics interface
interface DashboardStats {
  leads: {
    total: number;
    new7Days: number;
  };
  leadFinance: {
    contractAmount: number;    // 合同总额
    receivedAmount: number;    // 已收款总额
  };
  cases: {
    total: number;
    new7Days: number;
  };
  caseFinance: {
    contractAmount: number;    // 合同总额
    receivedAmount: number;    // 已收款总额
  };
}



const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load real statistics from API
      const apiStats = await getDashboardStatistics();

      // Transform API data to component format
      const realStats: DashboardStats = {
        leads: {
          total: apiStats.lead_statistics.total_leads,
          new7Days: apiStats.recent_activities.recent_leads,
        },
        leadFinance: {
          contractAmount: apiStats.finance_statistics.lead_contract_amount,
          receivedAmount: apiStats.finance_statistics.lead_received_amount,
        },
        cases: {
          total: apiStats.case_statistics.total_cases,
          new7Days: apiStats.recent_activities.recent_cases,
        },
        caseFinance: {
          contractAmount: apiStats.finance_statistics.case_contract_amount,
          receivedAmount: apiStats.finance_statistics.case_received_amount,
        },
      };

      setStats(realStats);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // Set empty data on error
      setStats({
        leads: { total: 0, new7Days: 0 },
        leadFinance: { contractAmount: 0, receivedAmount: 0 },
        cases: { total: 0, new7Days: 0 },
        caseFinance: { contractAmount: 0, receivedAmount: 0 },
      });
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return <div>加载中...</div>;
  }

  return (
    <div className="dashboard-container fade-in">
      {/* Statistics cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 8 }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 14 }}>总线索数</Text>
              <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                {stats?.leads.total || 0}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                7日新增 {stats?.leads.new7Days || 0} 个
              </Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 8 }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 14 }}>线索财务总额</Text>
              <Title level={2} style={{ margin: 0, color: '#52c41a' }}>
                ¥{(stats?.leadFinance.contractAmount || 0).toLocaleString()}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                已收款 ¥{(stats?.leadFinance.receivedAmount || 0).toLocaleString()}
              </Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 8 }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 14 }}>总案件数</Text>
              <Title level={2} style={{ margin: 0, color: '#faad14' }}>
                {stats?.cases.total || 0}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                7日新增 {stats?.cases.new7Days || 0} 个
              </Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 8 }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 14 }}>案件财务总额</Text>
              <Title level={2} style={{ margin: 0, color: '#722ed1' }}>
                ¥{(stats?.caseFinance.contractAmount || 0).toLocaleString()}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                已收款 ¥{(stats?.caseFinance.receivedAmount || 0).toLocaleString()}
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Charts and visualizations */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card title="线索跟进情况" className="dashboard-chart">
            <PersonalLeadDistributionChart />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card title="线索意向分布" className="dashboard-chart">
            <LeadIntentionDistributionChart />
          </Card>
        </Col>
      </Row>

    </div>
  );
};

export default Dashboard;